import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePeriodOptions } from "../../hooks/investments";

interface Props {
  value: string; // z.B. "ytd"
  onChange: (next: string) => void;
  periodStartIso?: string | null; // optional, vom Backend-DTO
  periodEndIso?: string | null;
}

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function PeriodSelector({ value, onChange, periodStartIso, periodEndIso }: Props) {
  const { t } = useTranslation();
  const options = usePeriodOptions();
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentQuarter = Math.floor(now.getUTCMonth() / 3) + 1; // 1..4
  const years = options.data?.years ?? [currentYear];
  const yearsDesc = [...years].sort((a, b) => b - a);

  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [quarterDropdownOpen, setQuarterDropdownOpen] = useState(false);

  const subline =
    periodStartIso && periodEndIso
      ? `${formatIsoDate(periodStartIso)} – ${formatIsoDate(periodEndIso)}`
      : null;

  function selectYear(y: number) {
    onChange(`year:${y}`);
    setYearDropdownOpen(false);
  }
  function selectQuarter(y: number, q: number) {
    onChange(`quarter:${y}-Q${q}`);
    setQuarterDropdownOpen(false);
  }

  const pillClass = (active: boolean) =>
    `px-3 py-1 text-sm rounded border ${
      active
        ? "bg-blue-600 text-white border-blue-600"
        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
    }`;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          className={pillClass(value === "total")}
          onClick={() => onChange("total")}
        >
          {t("investments.period.mode_total")}
        </button>
        <button
          type="button"
          className={pillClass(value === "ytd")}
          onClick={() => onChange("ytd")}
        >
          {t("investments.period.mode_ytd")}
        </button>
        <button
          type="button"
          className={pillClass(value === "12m")}
          onClick={() => onChange("12m")}
        >
          {t("investments.period.mode_12m")}
        </button>

        <div className="relative">
          <button
            type="button"
            className={pillClass(value.startsWith("year:"))}
            onClick={() => {
              setYearDropdownOpen((o) => !o);
              setQuarterDropdownOpen(false);
            }}
          >
            {value.startsWith("year:")
              ? `${t("investments.period.mode_year")} ${value.split(":")[1]}`
              : t("investments.period.mode_year")}
            <span className="ml-1 text-xs">▾</span>
          </button>
          {yearDropdownOpen && (
            <div className="absolute z-10 mt-1 bg-white border rounded shadow text-sm">
              {yearsDesc.map((y) => (
                <button
                  key={y}
                  type="button"
                  className="block px-3 py-1 hover:bg-slate-100 w-full text-left"
                  onClick={() => selectYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            className={pillClass(value.startsWith("quarter:"))}
            onClick={() => {
              setQuarterDropdownOpen((o) => !o);
              setYearDropdownOpen(false);
            }}
          >
            {value.startsWith("quarter:")
              ? (() => {
                  const m = value.match(/^quarter:(\d{4})-Q([1-4])$/);
                  return m ? `Q${m[2]} ${m[1]}` : t("investments.period.mode_quarter");
                })()
              : t("investments.period.mode_quarter")}
            <span className="ml-1 text-xs">▾</span>
          </button>
          {quarterDropdownOpen && (
            <div className="absolute z-10 mt-1 bg-white border rounded shadow text-sm max-h-64 overflow-y-auto">
              {yearsDesc.flatMap((y) =>
                [4, 3, 2, 1]
                  .filter((q) => y < currentYear || q <= currentQuarter)
                  .map((q) => (
                    <button
                      key={`${y}-Q${q}`}
                      type="button"
                      className="block px-3 py-1 hover:bg-slate-100 w-full text-left"
                      onClick={() => selectQuarter(y, q)}
                    >
                      Q{q} {y}
                    </button>
                  )),
              )}
            </div>
          )}
        </div>
      </div>
      {subline && <p className="text-xs text-slate-500">{subline}</p>}
    </div>
  );
}
